import { Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import {
  OrderDetailDataResponse,
  RouteDetailDataResponse,
  SummaryDataResponse,
} from 'src/hooks/useQueryBillingPerformances';

import { PeriodMode, tabLabels } from './constants';

const formatCurrency = (value: number) => `¥${value.toLocaleString()}`;

// 集計グリッドのカラム定義
export const getSummaryColumns = (analysisTab: number, periodMode: PeriodMode): GridColDef<SummaryDataResponse>[] => [
  // 日次モードのみ日付カラムを表示
  ...(periodMode === 'daily' ? [
    { field: 'date', headerName: '日付', width: 110 } as GridColDef<SummaryDataResponse>,
  ] : []),
  { field: 'name', headerName: tabLabels[analysisTab].replace('別', ''), width: periodMode === 'monthly' ? 200 : 160, flex: 1 },
  {
    field: 'billing',
    headerName: '請求',
    width: 130,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<SummaryDataResponse, number>) => (
      <Typography fontWeight="bold">{formatCurrency(params.value ?? 0)}</Typography>
    ),
  },
  {
    field: 'payment',
    headerName: '支払',
    width: 130,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<SummaryDataResponse, number>) => formatCurrency(params.value ?? 0),
  },
  {
    field: 'gross_profit',
    headerName: '粗利',
    width: 120,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<SummaryDataResponse, number>) => {
      const profit = params.value ?? 0;
      return (
        <Typography sx={{ color: profit < 0 ? 'error.main' : 'inherit' }}>
          {formatCurrency(profit)}
        </Typography>
      );
    },
  },
  {
    field: 'gross_profit_rate',
    headerName: '粗利率',
    width: 80,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<SummaryDataResponse, number>) => `${(params.value ?? 0).toFixed(1)}%`,
  },
  { field: 'cases', headerName: '件数', width: 80, align: 'right', headerAlign: 'right' },
  { field: 'quantity', headerName: '数量', width: 80, align: 'right', headerAlign: 'right' },
  {
    field: 'weight',
    headerName: '重量',
    width: 100,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<SummaryDataResponse, number>) => `${(params.value ?? 0).toLocaleString()}kg`,
  },
  {
    field: 'volume',
    headerName: '体積',
    width: 80,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<SummaryDataResponse, number>) => `${params.value ?? 0}m³`,
  },
  {
    field: 'distance',
    headerName: '距離',
    width: 100,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<SummaryDataResponse, number>) => `${(params.value ?? 0).toLocaleString()}km`,
  },
];

// 粗利カラム（明細共通）
const grossProfitDetailColumn = <T extends { billing: number; payment: number }>(
  _type: 'order' | 'route'
): GridColDef<T> => ({
  field: 'gross_profit',
  headerName: '粗利',
  width: 100,
  align: 'right',
  headerAlign: 'right',
  valueGetter: (_value: unknown, row: T) => (row.billing ?? 0) - (row.payment ?? 0),
  renderCell: (params: GridRenderCellParams<T, number>) => {
    const profit = params.value ?? 0;
    return (
      <Typography sx={{ color: profit < 0 ? 'error.main' : 'inherit' }}>
        {formatCurrency(profit)}
      </Typography>
    );
  },
});

// 案件単位の明細カラム
export const orderDetailColumns: GridColDef<OrderDetailDataResponse>[] = [
  {
    field: 'billing_tariff',
    headerName: '請求タリフ',
    minWidth: 100,
    flex: 0.6,
    renderCell: (params: GridRenderCellParams<OrderDetailDataResponse, string>) => {
      const v = params.value ?? '';
      return v === '' ? '' : v.toString().padStart(10, '0');
    },
  },
  {
    field: 'payment_tariff',
    headerName: '支払タリフ',
    minWidth: 100,
    flex: 0.6,
    renderCell: (params: GridRenderCellParams<OrderDetailDataResponse, string>) => {
      const v = params.value ?? '';
      return v === '' ? '' : v.toString().padStart(10, '0');
    },
  },
  { field: 'carrier', headerName: '運送会社', minWidth: 110, flex: 0.7 },
  { field: 'vehicle', headerName: '車両', minWidth: 130, flex: 0.8 },
  { field: 'driver', headerName: 'ドライバー', minWidth: 100, flex: 0.6 },
  { field: 'pickup_date', headerName: '積日', minWidth: 100, flex: 0.6 },
  { field: 'dropoff_date', headerName: '降日', minWidth: 100, flex: 0.6 },
  { field: 'pickup_name', headerName: '積地名', minWidth: 150, flex: 1.2 },
  { field: 'delivery_name', headerName: '降地名', minWidth: 150, flex: 1.2 },
  { field: 'pickup_prefecture', headerName: '積地(県)', minWidth: 90, flex: 0.6 },
  { field: 'delivery_prefecture', headerName: '降地(県)', minWidth: 90, flex: 0.6 },
  { field: 'quantity', headerName: '数量', minWidth: 65, flex: 0.4, align: 'right', headerAlign: 'right' },
  {
    field: 'weight',
    headerName: '重量',
    minWidth: 80,
    flex: 0.5,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<OrderDetailDataResponse, number>) => `${params.value ?? 0}kg`,
  },
  {
    field: 'volume',
    headerName: '体積',
    minWidth: 70,
    flex: 0.4,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<OrderDetailDataResponse, number>) => `${params.value ?? 0}m³`,
  },
  {
    field: 'billing',
    headerName: '請求',
    minWidth: 100,
    flex: 0.6,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<OrderDetailDataResponse, number>) => (
      <Typography fontWeight="bold">{formatCurrency(params.value ?? 0)}</Typography>
    ),
  },
  {
    field: 'payment',
    headerName: '支払',
    minWidth: 100,
    flex: 0.6,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<OrderDetailDataResponse, number>) => formatCurrency(params.value ?? 0),
  },
  grossProfitDetailColumn<OrderDetailDataResponse>('order'),
];

// ルート単位の明細カラム
export const routeDetailColumns: GridColDef<RouteDetailDataResponse>[] = [
  {
    field: 'billing_tariff',
    headerName: '請求タリフ',
    minWidth: 100,
    flex: 0.6,
    renderCell: (params: GridRenderCellParams<RouteDetailDataResponse, string>) => {
      const v = params.value ?? '';
      return v === '' ? '' : v.toString().padStart(10, '0');
    },
  },
  {
    field: 'payment_tariff',
    headerName: '支払タリフ',
    minWidth: 100,
    flex: 0.6,
    renderCell: (params: GridRenderCellParams<RouteDetailDataResponse, string>) => {
      const v = params.value ?? '';
      return v === '' ? '' : v.toString().padStart(10, '0');
    },
  },
  { field: 'carrier', headerName: '運送会社', minWidth: 110, flex: 0.7 },
  { field: 'vehicle', headerName: '車両', minWidth: 130, flex: 0.8 },
  { field: 'driver', headerName: 'ドライバー', minWidth: 100, flex: 0.6 },
  { field: 'pickup_date', headerName: '積日', minWidth: 100, flex: 0.6 },
  { field: 'dropoff_date', headerName: '降日', minWidth: 100, flex: 0.6 },
  {
    field: 'distance',
    headerName: '距離',
    minWidth: 90,
    flex: 0.6,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<RouteDetailDataResponse, number>) => `${params.value ?? 0}km`,
  },
  {
    field: 'time',
    headerName: '時間',
    minWidth: 80,
    flex: 0.5,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<RouteDetailDataResponse, number>) => `${params.value ?? 0}h`,
  },
  { field: 'quantity', headerName: '数量', minWidth: 80, flex: 0.5, align: 'right', headerAlign: 'right' },
  {
    field: 'weight',
    headerName: '重量',
    minWidth: 100,
    flex: 0.6,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<RouteDetailDataResponse, number>) => `${params.value ?? 0}kg`,
  },
  {
    field: 'volume',
    headerName: '体積',
    minWidth: 80,
    flex: 0.5,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<RouteDetailDataResponse, number>) => `${params.value ?? 0}m³`,
  },
  {
    field: 'billing',
    headerName: '請求',
    minWidth: 110,
    flex: 0.7,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<RouteDetailDataResponse, number>) => (
      <Typography fontWeight="bold">{formatCurrency(params.value ?? 0)}</Typography>
    ),
  },
  {
    field: 'payment',
    headerName: '支払',
    minWidth: 110,
    flex: 0.7,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<RouteDetailDataResponse, number>) => formatCurrency(params.value ?? 0),
  },
  grossProfitDetailColumn<RouteDetailDataResponse>('route'),
];
